import { FC, ReactElement, useEffect, useRef, useState, useSyncExternalStore } from "react";
import toast from "react-hot-toast";
import { fetchAsync, postAsync, postWithoutSign } from "../services/ApiHandler";
import { Button, Center, Checkbox, Group, Modal, ModalCloseButton, Radio } from "@mantine/core";
import { getPlanDescription, getPlanPrice, getPlanPriceText, getPlanSummary } from "../services/SubscriptionService";
import { getPlanName } from "../services/SubscriptionService";
import { StripeProduct } from "../model/StripeProduct";
import { useHeaderStore } from "./CommonHeader";
import { SetupIntentResponse } from "../model/SetupIntentResponse";
import { CardRegistrationDialog } from "./CardRegisterDialog";
import { Subscription } from "../model/Subscription";
import { StripeCheckoutResponse } from "../model/StripeCheckoutResponse";
import { isLoggedIn } from "../services/CookieHandler";

export class SubscriptionDialogProps {
  onPurchase?: () => void;
  onCancel?: () => void;
  upgradeTextVisible?: boolean = false;
  planId: string | undefined;
}

export const SubscriptionDialog: FC<SubscriptionDialogProps> = ({ onPurchase, onCancel, upgradeTextVisible, planId }) => {
  const [tosChecked,  setTosChecked] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedSpan, setSelectedSpan] = useState('month');
  const { account } = useHeaderStore();
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>([]);
  const [clientSecret, setClientSecret] = useState<string | undefined>(undefined);
  const [subscriptions, setSubscriptions] = useState<Subscription[] | undefined>(undefined);

  useEffect(() => {
    fetchStripeProducts();
    if (isLoggedIn()) {
      fetchSubscription();
    }
  }, []);

  async function fetchStripeProducts() {
    var products = await fetchAsync<StripeProduct[]>('Product/GetProductList');
    setStripeProducts(products);
  }

  async function fetchSubscription() {
    var subscription = await fetchAsync<Subscription[]>('Subscription/GetSubscriptionList');
    setSubscriptions(subscription);
  }

  async function subscribe() {
    const internalProductID = `${planId}_${selectedSpan}`;
    if(!tosChecked) {
      toast.error("利用規約および料金プランに同意してください");
      return;
    }

    var stripeProduct = stripeProducts.find(product => product.internalProductId === internalProductID);


    var checkoutResponse = await fetchAsync<StripeCheckoutResponse>('Stripe/GetCheckoutUrl?productId=' + stripeProduct?.productId + '&isTrial=' + (subscriptions?.length === 0 ? 'true' : 'false'));
    window.location.href = checkoutResponse.url;
  }

  function buildPriceText(span: string){
    var price = getPlanPrice(planId ?? '', span);
    var isSelected = selectedSpan === span;
    const isMonth = span === 'month';

    return <>
    <div className="flex justify-between items-center mb-3">
      <Radio color="#0909FF" value={span}></Radio>
      <div onClick={() => setSelectedSpan(span)} className={`clickable border rounded-md p-2 w-full ml-4 justify-between ${isSelected ? 'border-[#0909FF]' : ''} ${isSelected ? 'bg-[#B0B0F466]' : 'bg-white'}`}>
        <div className="flex justify-between">
          { (subscriptions !== undefined && subscriptions?.length === 0) &&
          <span>
            <span className="text-sm font-bold line-through">1ヶ月</span><span className="action-primary font-bold ml-1">7日間</span>
          </span>
          }
          { (subscriptions !== undefined && subscriptions?.length >= 1) &&
          <span className="text-sm font-bold">{isMonth ? '1ヶ月' : '12ヶ月' }</span>
          }
          { !isMonth &&
          <span className="text-s bg-[#F416BF] text-white font-bold text-xs py-1 px-2 rounded-md">¥{ price.discountRate }% OFF</span>
          }
        </div>
        <div className="flex justify-between">
          <span className="text-xs text-gray-500">{ isMonth ? `月お支払い ¥${price.monthly.toLocaleString('ja-JP')}` : `一括お支払い ¥${price.yearly.toLocaleString('ja-JP')}` }</span>
          { (subscriptions !== undefined && subscriptions?.length === 0) &&
          <span>
            <span className="action-primary font-bold mr-1">¥0</span>
            <span>
              <span className="text-sm font-bold line-through">¥{ price.monthly.toLocaleString('ja-JP') }</span>
              <span className="text-sm font-bold">/月</span>
            </span>
          </span>
          } 
          { (subscriptions !== undefined && subscriptions?.length >= 1) &&
          <span className="text-sm font-bold">¥{ span === 'month' ? price.monthly.toLocaleString('ja-JP') : price.monthlyForYear.toLocaleString('ja-JP') }/月</span>
          }
        </div>
      </div>
    </div>
    </>
  }

  return <>
    { clientSecret &&
    <Modal fullScreen opened={true} onClose={() => {}}>
      <CardRegistrationDialog clientSecret={clientSecret} onClose={() => {}} />
    </Modal>
    }
    <div className="w-full sm:w-[800px] inline-block justify-center text-primary">
      <div className="">
        <div className="block sm:flex" >
          <div className="w-full sm:w-[50%] text-left ml-0 sm:ml-4 mt-10 mb-4 sm:mb-10 pr-4">
            <div className="text-2xl font-bold">{getPlanName(planId ?? '')}プラン
              { (subscriptions !== undefined && subscriptions?.length === 0) &&
              <span className="text-2xl font-bold">無料トライアル</span>
              }
            </div>
            <div className="mt-4 text-gray-500">{getPlanSummary(planId ?? '')}</div>
            <div className="mt-4 text-sm text-gray-500 whitespace-pre-wrap">
              {getPlanDescription(planId ?? '')}
            </div>
          </div>
          <div className="w-full sm:w-[50%] items-center justify-center px-4 bg-[#F8F9FA] pt-1">
            { ( subscriptions !== undefined && subscriptions?.length >= 1 && upgradeTextVisible) &&
            <div className="text-left text-sm mt-4 font-bold">
              <div>クレジットを使い切りました。</div>
              <div>
                <span className="text-[#0909FF] text-lg" style={{textDecoration: 'underline', textDecorationColor: '#CFCFFF', textDecorationThickness: '4px' }}>アップグレードで</span>利用できます。
              </div>
            </div>
            }
            { (subscriptions !== undefined && subscriptions?.length === 0) &&
            <div className="text-left text-sm mt-4 font-bold">
              <div className="gradation-text">有料プランの内容が7日間無料で利用できます！</div>
              <div>
                {/* <span>{ getPlanName(planId ?? '') }プランの内容が</span> */}
                <span>トライアル期間経過まで</span>
                <span className="text-[#0909FF] text-lg" style={{textDecoration: 'underline', textDecorationColor: '#CFCFFF', textDecorationThickness: '4px' }}>決済は実行されません。</span>
              </div>
            </div>
            }
            <div className=" justify-center mt-8">
              <Radio.Group value={selectedSpan} onChange={(e) => setSelectedSpan(e)}>
              { (subscriptions !== undefined && subscriptions?.length >= 1) &&
                buildPriceText('year')
              }
              {buildPriceText('month') }
              </Radio.Group>
              <div className="flex justify-center items-center mt-12">
                <Checkbox
                  color="#0909FF"
                  size="sm"
                  checked={tosChecked}
                  label="利用規約および料金プランに同意する"
                  onChange={(event) => setTosChecked(event.currentTarget.checked)}
                />
                </div>
                { (subscriptions !== undefined && subscriptions?.length === 0) &&
                <div className="mt-4 pb-4">
                  <Button className="gradation-button text-white" style={{fontWeight: '700'}} onClick={subscribe} disabled={ processing } justify="center" fullWidth>無料トライアルのお手続きに進む</Button>
                </div>
                }
                { (subscriptions !== undefined && subscriptions?.length >= 1) &&
                <div className="mt-4 pb-4">
                  <Button className="gradation-button text-white" style={{fontWeight: '700'}} onClick={subscribe} disabled={ processing } justify="center" fullWidth>プランを変更する</Button>
                </div>
                }
              </div>
          </div>
        </div>
      </div>
    </div>
  </>;

}
